import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Link2,
  ServerCog,
  ShieldCheck,
  Users,
} from 'lucide-react';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function HealthItem({ label, value, good = true, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-2 text-sm font-black text-slate-950">{value}</p>
        </div>
        <div className={`rounded-xl p-2 ${good ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export default function DirectoryIdentityHealth({ health }) {
  if (!health) return null;

  const readiness = health.readiness || {};
  const connection = health.connection || {};
  const sync = health.sync || {};
  const counts = health.counts || {};
  const coverage = health.mapping_coverage || {};
  const stale = health.stale_objects || {};
  const servicePrincipal = connection.service_principal || {};
  const staleTotal = counts.stale_total || 0;

  return (
    <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-black text-slate-950">Microsoft directory operational status</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Microsoft determines authentication and synchronization eligibility. PII Platform remains the source of RBAC authorization.
          </p>
        </div>
        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ring-1 ${
          readiness.ready
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
            : 'bg-amber-50 text-amber-800 ring-amber-200'
        }`}>
          {readiness.ready ? 'Directory ready' : 'Action required'}
        </span>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <HealthItem
          label="Tenant connection"
          value={readiness.tenant_connected ? 'Connected' : 'Not connected'}
          good={readiness.tenant_connected}
          icon={Link2}
        />
        <HealthItem
          label="Manual setup"
          value={readiness.manual_setup_confirmed ? 'Confirmed' : 'Incomplete'}
          good={readiness.manual_setup_confirmed}
          icon={CheckCircle2}
        />
        <HealthItem
          label="Service principal"
          value={servicePrincipal.resolved ? 'Resolved' : readiness.service_principal_required ? 'Unresolved' : 'Not required'}
          good={servicePrincipal.resolved || !readiness.service_principal_required}
          icon={ServerCog}
        />
        <HealthItem
          label="Schedule"
          value={readiness.schedule_enabled ? 'Enabled' : 'Manual only'}
          good={readiness.schedule_enabled}
          icon={Clock3}
        />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <HealthItem
          label="Synced users"
          value={`${counts.directory_users || 0} total · ${counts.directory_users_ready || 0} RBAC ready`}
          good={(counts.directory_users || 0) > 0}
          icon={Users}
        />
        <HealthItem
          label="Synced groups"
          value={`${counts.directory_groups || 0} groups`}
          good={(counts.directory_groups || 0) > 0}
          icon={Users}
        />
        <HealthItem
          label="Mapping coverage"
          value={`${coverage.mapped_group_count || 0}/${coverage.eligible_group_count || 0} groups (${coverage.coverage_percentage || 0}%)`}
          good={(coverage.unmapped_group_count || 0) === 0 && (coverage.eligible_group_count || 0) > 0}
          icon={ShieldCheck}
        />
        <HealthItem
          label="Stale objects"
          value={`${staleTotal} flagged`}
          good={staleTotal === 0}
          icon={AlertTriangle}
        />
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <p className="font-bold text-slate-950">Latest synchronization</p>
          <p className="mt-2">Last success: {formatDate(sync.last_success_at || sync.latest_success?.completed_at)}</p>
          <p className="mt-1">Last failure: {formatDate(sync.last_failed_at || sync.latest_failure?.completed_at)}</p>
          <p className="mt-1">Next scheduled run: {formatDate(sync.next_run_at)}</p>
          {sync.current_run && (
            <p className="mt-2 font-semibold text-sky-700">
              Active run {sync.current_run.id} · {sync.current_run.status}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-bold">Non-destructive reconciliation</p>
          <p className="mt-2 leading-6">
            Automatic revocation is not enabled. Deleting a mapping or detecting a stale Microsoft assignment does not remove an existing local PII group, disable a user, or revoke sessions.
          </p>
        </div>
      </div>

      {((coverage.unmapped_groups || []).length > 0 || staleTotal > 0) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {(coverage.unmapped_groups || []).length > 0 && (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
              <p className="text-sm font-black text-orange-950">Unmapped Microsoft groups</p>
              <div className="mt-2 space-y-1 text-xs text-orange-900">
                {coverage.unmapped_groups.map((group) => (
                  <p key={group.id}>{group.display_name}</p>
                ))}
              </div>
            </div>
          )}

          {staleTotal > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-black text-amber-950">Stale Microsoft objects</p>
              <div className="mt-2 space-y-1 text-xs text-amber-900">
                {(stale.users || []).map((user) => (
                  <p key={`user-${user.id}`}>User: {user.display_name || user.email}</p>
                ))}
                {(stale.groups || []).map((group) => (
                  <p key={`group-${group.id}`}>Group: {group.display_name}</p>
                ))}
                {(stale.membership_count || 0) > 0 && (
                  <p>{stale.membership_count} stale group membership(s)</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
